import { z } from 'zod';
import { AuditLogEntrySchema } from './audit.js';
import { CaseSchema, CaseStatusSchema } from './case.js';
import { SourceSchema } from './source.js';
import { AppRoleSchema, UserSchema } from './access.js';
import {
  AuditLogIdSchema,
  CaseIdSchema,
  EmailSchema,
  IsoTimestampSchema,
  UserIdSchema,
} from './common.js';

export const AdminSourceSummarySchema = z.object({
  source: SourceSchema,
  eventCount: z.number().int().min(0),
  sourceItemCount: z.number().int().min(0),
  importJobCount: z.number().int().min(0),
});
export type AdminSourceSummary = z.infer<typeof AdminSourceSummarySchema>;

export const AdminCaseSummarySchema = z.object({
  case: CaseSchema,
  sources: z.array(AdminSourceSummarySchema),
  totalEvents: z.number().int().min(0),
  totalSourceItems: z.number().int().min(0),
});
export type AdminCaseSummary = z.infer<typeof AdminCaseSummarySchema>;

export const AdminActivityEntrySchema = AuditLogEntrySchema.extend({
  actorEmail: z.string().email().optional(),
  actorDisplayName: z.string().optional(),
  caseName: z.string().optional(),
});
export type AdminActivityEntry = z.infer<typeof AdminActivityEntrySchema>;

export const AdminActivityCursorSchema = z.object({
  timestamp: IsoTimestampSchema,
  id: AuditLogIdSchema,
});
export type AdminActivityCursor = z.infer<typeof AdminActivityCursorSchema>;

export const ListAdminActivityInputSchema = z.object({
  cursor: AdminActivityCursorSchema.optional(),
});
export type ListAdminActivityInput = z.infer<typeof ListAdminActivityInputSchema>;

export const ListAdminActivityResultSchema = z.object({
  activities: z.array(AdminActivityEntrySchema),
  nextCursor: AdminActivityCursorSchema.optional(),
});
export type ListAdminActivityResult = z.infer<typeof ListAdminActivityResultSchema>;

export const AdminOverviewResultSchema = z.object({
  users: z.array(UserSchema),
  cases: z.array(AdminCaseSummarySchema),
  activityCount: z.number().int().min(0),
});
export type AdminOverviewResult = z.infer<typeof AdminOverviewResultSchema>;

export const AdminUpdateUserInputSchema = z.object({
  userId: UserIdSchema,
  displayName: z.string().min(1, 'Visningsnavn kan ikke være tomt'),
  appRole: AppRoleSchema,
  disabled: z.boolean(),
});
export type AdminUpdateUserInput = z.infer<typeof AdminUpdateUserInputSchema>;

export const AdminCreateUserInputSchema = z.object({
  email: EmailSchema,
  displayName: z.string().min(1, 'Visningsnavn kan ikke være tomt'),
  appRole: AppRoleSchema.default('user'),
});
export type AdminCreateUserInput = z.infer<typeof AdminCreateUserInputSchema>;

export const AdminUpdateCaseInputSchema = z.object({
  caseId: CaseIdSchema,
  navn: z.string().min(1, 'Saksnavn kan ikke være tomt'),
  saksnummer: z.string().min(1, 'Saksnummer kan ikke være tomt'),
  klient: z.string().min(1, 'Klient kan ikke være tom'),
  status: CaseStatusSchema,
});
export type AdminUpdateCaseInput = z.infer<typeof AdminUpdateCaseInputSchema>;
